import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import * as zVal from '../lib/validations';
import { getMemberPlanSearchYears, getMemberPlanSearchCounties } from '../lib/api';
import { captureError } from '../lib/sentry';
import '../App.css';
import Text from '../components/ui/text';
import Select from '../components/ui/select';
import Input from '../components/ui/input';
import Button from '../components/ui/button';
import Spinner from '../components/ui/spinner';
import { useTranslation } from 'react-i18next';

const formSchema = zod
  .object({
    year: zVal.selectWithNumberValueRequired(),
    zipCode: zVal.zipCodeRequired(),
    county: zVal.textRequired(),
  });
const resolver = zodResolver(formSchema);

function App() {
  const { t } = useTranslation();
  const [zipCode, setZipCode] = useState();
  const [countySelectedKeys, setCountySelectedKeys] = useState([]);
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {},
    resolver
  });

  // Form Validation
  const {
    getFieldState,
    getValues,
    setError,
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors, isValidating, isValid }
  } = methods;

  const watchYear = watch('year');
  const watchCounty = watch('county');

  // Request Years Data
  const { isPending: yearsIsPending, isError: yearsIsError, data: years, error: yearsError } = useQuery({
    queryKey: ['memberPlanSearchGetYears'],
    queryFn: async () => {
      try {
        const response = await getMemberPlanSearchYears();
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'member-plan-search', query: 'memberPlanSearchGetYears' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
  });

  // Request Counties Data
  const { isPending: countiesIsPending, isError: countiesIsError, data: counties, error: countiesError  } = useQuery({
    queryKey: ['memberPlanSearchGetCounties', zipCode],
    queryFn: async () => {
      try {
        const response = await getMemberPlanSearchCounties(zipCode);
        if (!response?.success) {
          setError("zipCode", { type: "custom", message: t('Invalid zip code') })
        }
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'member-plan-search', query: 'memberPlanSearchGetCounties' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
    enabled: !!zipCode,
  });

  const handleZipCodeBlur = () => {
    const { invalid, isDirty } = getFieldState('zipCode');
    if (!invalid && isDirty) setZipCode(getValues('zipCode'));
  }

  // Select Lists
  const yearList = useMemo(() => years?.data?.map(i => ({ label: i.name, value: i.id})), [years]);
  const countyList = useMemo(() => counties?.data?.map(i => ({ label: i.name, value: i.id})), [counties]);

  const onSubmit = useCallback((data) => {
    const allPlans = document.querySelectorAll(`[data-year][data-county]`);
    if (allPlans.length > 0) {
      allPlans.forEach((element) => {
        element.style.display = "none";
      });
    }
    const filteredPlans = document.querySelectorAll(`[data-year="${data.year}"]`);
    if (filteredPlans.length > 0) {
      filteredPlans.forEach((element) => {
        if (element.dataset?.county?.includes(data.county)) {
          element.style.display = "block";
        }
      });
    }
  }, []);

  useEffect(() => {
    if (yearList?.length === 1) {
      setValue('year', yearList[0].value, { shouldValidate: true });
    }
  }, [yearList, setValue]);

  useEffect(() => {
    if (counties?.data?.length === 1) {
      setValue('county', counties.data[0].id);
    }
  }, [counties, setValue]);


  console.log('watchCounty', watchCounty);
  return (
    <div className='component-container font-monserrat w-full h-auto flex flex-col justify-start items-start font-normal'>
      <div className='w-full h-auto flex flex-col gap-5'>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5 pb-5 border-b border-gray-600'>
            <div className='flex flex-col sm:flex-row gap-5'>
              <div className='flex-1 flex flex-col justify-start items-start'>
                <Text type='label' htmlFor='year'>Year</Text>
                <Select
                  key={yearList ? `year-${yearList[0].value}` : 'year-pending'}
                  {...register('year')}
                  size='sm'
                  label={<Text>Select a year</Text>}
                  options={yearList}
                  selectionMode="single"
                  disallowEmptySelection={true}
                  errorMessage={errors.year?.message || null}
                  isInvalid={errors.year ? true : false}
                  isDisable={!yearList}
                  selectedKeys={watchYear != null && watchYear !== '' ? [String(watchYear)] : []}
                />
              </div>
              <div className='flex-1 flex flex-col justify-start items-start'>
                <Text type='label' htmlFor='county'>Zip Code</Text>
                <Input 
                  {...register('zipCode')} 
                  label={<Text>Zip Code</Text>}
                  size='sm'
                  errorMessage={errors.zipCode?.message || null}
                  isInvalid={errors.zipCode ? true : false}
                  onBlur={handleZipCodeBlur}
                  endContent={
                    (zipCode && countiesIsPending && <div className='flex flex-row justify-end'><Spinner size='w-5 h-5' /></div>)
                  }
                />
              </div>
              <div className='flex-1 flex flex-col justify-start items-start'>
                <Text type='label' htmlFor='county'>County</Text>
                <Select
                  //ref={zipCode || '1'}
                  {...register('county')}
                  size='sm'
                  label={<Text>Select a county</Text>} 
                  options={countyList}
                  selectionMode="single"
                  disallowEmptySelection={true}
                  errorMessage={errors.county?.message || null}
                  isInvalid={errors.county ? true : false}
                  isDisable={!countyList}
                  selectedKeys={[watchCounty]}
                />
              </div>
            </div>
            <div className='flex flex-row justify-center items-center'>
              <Button type='submit' color='primary' radius='full' ><Text>View plans</Text></Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default App
